// src/components/ui/Tooltip.tsx
import React from 'react';
import ReactDOM from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { usePopper } from 'react-popper';

interface TooltipProps {
  text?: string | React.ReactNode;
  textKey?: string;
  textOptions?: Record<string, unknown>;
  /** Must be a single React Element that can accept a ref */
  children: React.ReactElement;
  tooltipClassName?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  disabled?: boolean;
  forceVisible?: boolean;
}

function setRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (typeof ref === 'function') {
    ref(value);
    return;
  }

  if (ref && typeof ref === 'object') {
    (ref as React.MutableRefObject<T | null>).current = value;
  }
}

function composeEventHandlers<E>(
  originalHandler: ((event: E) => void) | undefined,
  tooltipHandler: (event: E) => void
) {
  return (event: E) => {
    originalHandler?.(event);
    tooltipHandler(event);
  };
}

/**
 * A robust and performant Tooltip component using Popper.js for positioning.
 * It attaches event listeners directly to the child element without an extra wrapper div.
 */
export const Tooltip: React.FC<TooltipProps> = ({
  text: directText,
  textKey,
  textOptions,
  children,
  tooltipClassName = '',
  position = 'top',
  delay = 150, // A snappier delay
  disabled = false,
  forceVisible = false,
}) => {
  const { t } = useTranslation('common');
  const [isVisible, setIsVisible] = React.useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const tooltipId = React.useId();

  // --- Popper.js Integration for Robust Positioning ---
  const [referenceElement, setReferenceElement] = React.useState<HTMLElement | null>(null);
  const [popperElement, setPopperElement] = React.useState<HTMLElement | null>(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: position,
    modifiers: [
      { name: 'offset', options: { offset: [0, 8] } }, // 8px offset
      { name: 'preventOverflow', options: { padding: 8 } }, // 8px from viewport edge
      { name: 'flip', options: { padding: 8 } }, // Flip if not enough space
    ],
  });
  // --- End Popper.js Integration ---

  const resolvedText = textKey ? t(textKey, textOptions) : directText;

  const showTooltip = React.useCallback(() => {
    if (disabled || !resolvedText) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  }, [disabled, resolvedText, delay]);

  const hideTooltip = React.useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsVisible(false);
  }, []);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const isTooltipShown = (isVisible || forceVisible) && !disabled && !!resolvedText;
  const childProps = children.props as React.HTMLAttributes<HTMLElement> & {
    'aria-describedby'?: string;
  };
  const childRef = (children as React.ReactElement & { ref?: React.Ref<HTMLElement> }).ref;
  const mergedAriaDescribedBy = [childProps['aria-describedby'], isTooltipShown ? tooltipId : undefined]
    .filter(Boolean)
    .join(' ') || undefined;

  // Clone the child element to attach event listeners and the ref for Popper.
  // This avoids wrapping the child in an unnecessary div.
  const triggerElement = React.cloneElement(children, {
    ref: (node: HTMLElement | null) => {
      setReferenceElement(node);
      setRef(childRef, node);
    },
    onMouseEnter: composeEventHandlers(childProps.onMouseEnter, showTooltip),
    onMouseLeave: composeEventHandlers(childProps.onMouseLeave, hideTooltip),
    onFocus: composeEventHandlers(childProps.onFocus, showTooltip),
    onBlur: composeEventHandlers(childProps.onBlur, hideTooltip),
    'aria-describedby': mergedAriaDescribedBy,
  });

  const tooltipContent = (
    <AnimatePresence>
      {isTooltipShown && (
        // This outer div is what Popper.js positions
        <div
          ref={setPopperElement}
          style={styles.popper}
          {...attributes.popper}
          className="z-[9999]" // Use a very high z-index
          role="tooltip"
          id={tooltipId}
        >
          {/* This inner div is for styling and animation */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.1, ease: "easeOut" }}
            className={clsx(
              // Base
              'px-3 py-1.5 text-xs font-medium whitespace-nowrap shadow-xl',

              // Neobrutalism
              'text-white bg-gray-900 rounded-md',

              // Liquid glass overrides
              '[.theme-liquid-glass_&]:bg-slate-800/90 [.theme-liquid-glass_&]:backdrop-blur-xl',
              '[.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20 [.theme-liquid-glass_&]:rounded-xl',

              tooltipClassName
            )}
          >
            {resolvedText}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      {triggerElement}
      {typeof document !== 'undefined' && ReactDOM.createPortal(tooltipContent, document.body)}
    </>
  );
};
